import * as React from 'react';
import { Sheet, SheetContent } from '../ui/sheet';
import { cn } from '../../lib/utils';
import { useResizable } from '../../hooks/useResizable';
import { ResizeHandle } from '../ResizeHandle/ResizeHandle';

const widthPresets = {
  sm: 'min-[420px]:!w-[320px]',
  md: 'min-[420px]:!w-[420px]',
  lg: 'min-[420px]:!w-[600px]',
  full: '!w-screen'
} as const;

export interface CustomDrawerProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  side?: 'left' | 'right' | 'top' | 'bottom';
  width?: 'sm' | 'md' | 'lg' | 'full' | number;
  className?: string;
  resizable?: boolean;
  minWidth?: number;
  maxWidth?: number;
  defaultWidth?: number;
  storageKey?: string;
}

export function CustomDrawer({
  open,
  onClose,
  children,
  side = 'right',
  width = 'md',
  className,
  resizable = false,
  minWidth = 320,
  maxWidth = 800,
  defaultWidth = 420,
  storageKey
}: Readonly<CustomDrawerProps>): React.ReactElement {
  const [isMobile, setIsMobile] = React.useState(false);
  const [viewportWidth, setViewportWidth] = React.useState(
    globalThis.window === undefined ? 1024 : globalThis.window.innerWidth
  );

  React.useEffect(() => {
    const handleResize = (): void => {
      setIsMobile(window.innerWidth < 768);
      setViewportWidth(window.innerWidth);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const {
    width: resizableWidth,
    isResizing,
    handleMouseDown
  } = useResizable({
    minWidth,
    maxWidth,
    defaultWidth,
    storageKey,
    disabled: !resizable || isMobile
  });

  const shouldUseResizable = resizable && !isMobile;
  const widthClass =
    typeof width === 'number' ? `sm:w-[${width}px]` : widthPresets[width];

  const shouldUseFullScreen =
    shouldUseResizable && resizableWidth >= viewportWidth - 20;

  const widthStyle = React.useMemo(() => {
    if (shouldUseResizable) {
      if (shouldUseFullScreen) {
        return { width: '100vw' };
      }
      return { width: `${resizableWidth}px` };
    }
    if (typeof width === 'number') {
      return { width: `${width}px` };
    }
    return undefined;
  }, [shouldUseResizable, resizableWidth, width, shouldUseFullScreen]);

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent
        side={side}
        showCloseButton={false}
        className={cn(
          'max-md:!w-screen p-0 sm:!max-w-none group',
          !resizable &&
            !shouldUseResizable &&
            typeof width !== 'number' &&
            widthClass,
          isResizing && '!transition-none !duration-0',
          className
        )}
        style={widthStyle}
      >
        {shouldUseResizable && !shouldUseFullScreen && side === 'right' && (
          <ResizeHandle
            onMouseDown={handleMouseDown}
            isResizing={isResizing}
            side='left'
          />
        )}
        {shouldUseResizable && !shouldUseFullScreen && side === 'left' && (
          <ResizeHandle
            onMouseDown={handleMouseDown}
            isResizing={isResizing}
            side='right'
          />
        )}
        {children}
      </SheetContent>
    </Sheet>
  );
}

export default CustomDrawer;
